import React, { useEffect, useState } from "react";
import {
  Row,
  Col,
  Card,
  Badge,
  ProgressBar,
  Button,
  Table,
  Spinner,
  Alert,
} from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { getReviwQueueDeshboard } from "../../../../../../../redux/slices/tdsSlice";
import { Pencil, Trash, Eye, Download as DownloadIcon } from "lucide-react";

const LoanModule = () => {
  const dispatch = useDispatch();
  const [loans, setLoans] = useState([]);

  const { reviewQueueData, reviewQueueLoading, reviewQueueError } = useSelector(
    (state) => state.tds
  );

  const storedUser = JSON.parse(localStorage.getItem("tdsUser") || "{}");
  const staffId = storedUser.staffId || storedUser.staff_id || storedUser.id;

  useEffect(() => {
    if (staffId) {
      dispatch(
        getReviwQueueDeshboard({
          path: "admin/getReviwQueueDeshbord",
          data: {
            userId: storedUser.user_id || "",
            staffId: Number(staffId),
            employeeId: "FY" + (storedUser.financialYear || "2025-2026"),
            section: "all",
            financialYear: storedUser.financialYear || "2025-2026",
          },
        })
      );
    }
  }, [dispatch, staffId]);

  useEffect(() => {
    if (reviewQueueData) {
      const apiData = reviewQueueData?.data || reviewQueueData || {};
      const homeLoan = apiData.sections?.homeLoan || {};
      
      if (homeLoan.fields) {
        setLoans([{
          id: 1,
          employeeName: apiData.employeeInfo?.employeeName || storedUser.employeeName || "N/A",
          amount: homeLoan.fields?.claimedInterest || 0,
          purpose: "Home Loan Interest (Sec 24B)",
          donationType: "Annual",
          status: homeLoan.status || "Pending",
          donatedDate: new Date().toISOString().split('T')[0],
        }]);
      }
    }
  }, [reviewQueueData]);

  // Loading State
  if (reviewQueueLoading) {
    return (
      <div
        className="d-flex flex-column justify-content-center align-items-center"
        style={{ minHeight: "100vh", backgroundColor: "#0a0a1a" }}
      >
        <Spinner animation="border" variant="primary" className="mb-4" />
        <h5 className="text-white">Loading Loan Details...</h5>
        <p className="text-muted">Please wait while we fetch your data</p>
      </div>
    );
  }

  // Error State
  if (reviewQueueError) {
    return (
      <div
        className="text-center py-5"
        style={{ minHeight: "100vh", backgroundColor: "#0a0a1a" }}
      >
        <h4 className="text-danger mb-3">Failed to Load Data</h4>
        <p className="text-muted mb-4">
          {reviewQueueError?.message || "Something went wrong while fetching loan data."}
        </p>
        <Button variant="outline-light" onClick={() => window.location.reload()}>
          Try Again
        </Button>
      </div>
    );
  }

  // No Data State
  if (!reviewQueueData || loans.length === 0) {
    return (
      <div
        className="d-flex flex-column justify-content-center align-items-center text-center"
        style={{ minHeight: "100vh", backgroundColor: "#0a0a1a", padding: "2rem" }}
      >
        <div className="mb-5">
          <i className="display-1 text-muted">📅</i>
        </div>
        <h3 className="text-white mb-3">No Loan Details Available</h3>
        <p className="text-muted lead mb-4" style={{ maxWidth: "600px" }}>
          There are no loan declarations submitted or configured for this employee yet.
        </p>
        <p className="text-info mb-5">
          Please submit your home loan details or check back later. This feature will be updated shortly.
        </p>
        <Alert variant="info" className="mx-auto" style={{ maxWidth: "500px" }}>
          <strong>Give us some time!</strong> We're processing your loan information.
        </Alert>
        <Button variant="outline-primary" size="lg" className="mt-4" onClick={() => window.location.reload()}>
          Refresh
        </Button>
      </div>
    );
  }

  const handleView = (id) => {
    alert(`Viewing loan ID: ${id}`);
  };

  const handleEdit = (id) => {
    alert(`Editing loan ID: ${id}`);
  };

  const handleDelete = (id) => {
    if (window.confirm(`Are you sure you want to delete loan ID: ${id}?`)) {
      setLoans(loans.filter(loan => loan.id !== id));
    }
  };

  const handleDownload = (id) => {
    alert(`Downloading loan ID: ${id}`);
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white p-6 max-w-7xl mx-auto">
      <h2 className="text-3xl font-bold text-center mb-10">Employee Loan Details</h2>

      <div className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden">
        <div className="bg-gradient-to-r from-green-700 to-emerald-700 p-6">
          <h3 className="text-xl font-bold flex items-center gap-3">
            <span className="text-3xl">🏦</span> Home Loan Declarations
          </h3>
        </div>

        <div className="overflow-x-auto">
          <Table bordered hover variant="dark" className="mb-0">
            <thead>
              <tr>
                <th className="text-center">ID</th>
                <th>Employee Name</th>
                <th>Amount (₹)</th>
                <th>Purpose</th>
                <th>Type</th>
                <th>Date</th>
                <th>Status</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loans.map((loan) => (
                <tr key={loan.id}>
                  <td className="text-center align-middle">#{loan.id}</td>
                  <td className="align-middle">{loan.employeeName}</td>
                  <td className="align-middle">
                    <span className="text-success fw-bold">
                      ₹{loan.amount.toLocaleString()}
                    </span>
                  </td>
                  <td className="align-middle">{loan.purpose}</td>
                  <td className="align-middle">
                    <Badge bg="info" className="px-3 py-2">
                      {loan.donationType}
                    </Badge>
                  </td>
                  <td className="align-middle">{loan.donatedDate}</td>
                  <td className="align-middle">
                    <Badge 
                      bg={loan.status === 'Completed' ? 'success' : 'warning'} 
                      className="px-3 py-2"
                    >
                      {loan.status}
                    </Badge>
                  </td>
                  <td className="align-middle">
                    <div className="d-flex justify-content-center gap-2">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="d-flex align-items-center gap-1"
                        onClick={() => handleView(loan.id)}
                      >
                        <Eye size={16} /> View
                      </Button>
                      <Button
                        variant="outline-warning"
                        size="sm"
                        className="d-flex align-items-center gap-1"
                        onClick={() => handleEdit(loan.id)}
                      >
                        <Pencil size={16} /> Edit
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        className="d-flex align-items-center gap-1"
                        onClick={() => handleDelete(loan.id)}
                      >
                        <Trash size={16} /> Delete
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </div>

      <div className="text-center mt-10">
        <p className="text-info">
          🏦 Manage your home loan declarations for tax benefits
        </p>
      </div>
    </div>
  );
};

export default LoanModule;